'use client';
import { useState, useRef, useEffect, ReactNode } from 'react';
import cn from 'classnames';
import styles from './ButtonWithLabel.module.scss';

interface ButtonWithLabelProps {
    children: ReactNode;
    onClick: React.MouseEventHandler<HTMLButtonElement>;
    labelText: string;
    className?: string;
    ariaPressed?: boolean;
    doNotTrack?: true | null;
}

export function ButtonWithLabel({ children, onClick, labelText, className, ariaPressed, doNotTrack=null }: ButtonWithLabelProps) {
    const timeoutRef = useRef<number | null>(null);
    const [showLabel, setShowLabel] = useState(false);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const buttonLabelStyles = cn(styles.buttonLabel, {
        [styles.showLabel]: showLabel,
    });

    function handleMouseEvent(type: string) {
        if (type === 'enter') {
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = window.setTimeout(() => {
                setShowLabel(true);
            }, 400);
        }

        if (type === 'leave') {
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            setShowLabel(false);
        }
    }

    return (
        <>
            <button
                className={className}
                onClick={onClick}
                onMouseEnter={() => handleMouseEvent('enter')}
                onMouseLeave={() => handleMouseEvent('leave')}
                aria-label={`${labelText} - button`}
                aria-pressed={ariaPressed}
                data-donottrack={doNotTrack}
            >
                {children}
            </button>
            <span className={buttonLabelStyles}>{labelText}</span>
        </>
    );
}
